/**
 * City Repository
 * Handles all city-related API calls
 */

import { apiClient } from '../../client'
import type { PaginatedResponse } from '../role.repository'
import { Region } from '@/libs/api/repositories/universe/region.repository'

export interface City {
    id: number
    region_id: number
    en_name: string
    ar_name?: string | null
    status: number
    created_by?: number | null
    updated_by?: number | null
    created_at: Date
    updated_at: Date
    region: Region
    ref_code?: string
}

export interface CreateCityRequest {
    region_id: number
    en_name: string
    ar_name?: string
    status?: number
    ref_code?: string

    // Note: created_by is automatically set by backend from logged-in user
}

export interface UpdateCityRequest {
    region_id?: number
    en_name?: string
    ar_name?: string
    status?: number
    ref_code?: string

    // Note: updated_by is automatically set by backend from logged-in user
}

class CityRepository {
    /**
     * Get all cities with pagination
     */
    async getAll(page: number = 1, limit: number = 10, filters?: any): Promise<PaginatedResponse<City>> {
        const params = new URLSearchParams({
            page: page.toString(),
            limit: limit.toString(),
        })

        // Add filters if provided
        if (filters) {
            if (filters.search) {
                params.append('search', filters.search)
            }
            if (filters.status) {
                params.append('status', filters.status)
            }
            if (filters.region_id) {
                params.append('region_id', filters.region_id)
            }
        }

        return apiClient.get<PaginatedResponse<City>>(`/cities?${params.toString()}`)
    }

    /**
     * Get city by ID
     */
    async getById(id: number): Promise<City> {
        return apiClient.get<City>(`/cities/${id}`)
    }

    /**
     * Create a new city
     */
    async create(data: CreateCityRequest): Promise<City> {
        return apiClient.post<City>('/cities', data)
    }

    /**
     * Update an existing city
     */
    async update(id: number, data: UpdateCityRequest): Promise<City> {
        return apiClient.put<City>(`/cities/${id}`, data)
    }

    /**
     * Delete a city
     */
    async delete(id: number): Promise<{ message: string }> {
        return apiClient.delete<{ message: string }>(`/cities/${id}`)
    }

    /**
     * Export cities to Excel
     */
    async exportToExcel(filters?: { search?: string; status?: string; region_id?: string }): Promise<Blob> {
        const params = new URLSearchParams()
        if (filters?.search) {
            params.append('search', filters.search)
        }
        if (filters?.status) {
            params.append('status', filters.status)
        }
        if (filters?.region_id) {
            params.append('region_id', filters.region_id)
        }

        const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:5000'
        const baseUrl = `${API_BASE_URL}/cities/export/excel`;
        const queryString = params.toString();
        const url = queryString ? `${baseUrl}?${queryString}` : baseUrl;

        return apiClient.getBlob(url)
    }
}

// Export singleton instance
export const cityRepository = new CityRepository()

